import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { useT } from '../i18n/i18n';
import type { ProgramView } from '../programs/types';
import { MEASURE_LABEL, createActivity, updateActivity, type Activity, type ActivityKind, type Measure } from './api';
import { orNull } from './GoalDates';

export const ACTIVITY_KIND_LABEL: Record<ActivityKind, string> = {
  PROGRAM: 'EasyLearning program',
  AWAY: 'Away from the app',
  TEXT: 'Text',
};

const TEMPLATE = [
  'What counts as right (with examples):',
  'How to present it:',
  'Help to give and how to reduce it:',
  'After a wrong answer:',
  'Reward:',
  'When it counts as learned:',
];

export default function ActivityEditor({ learnerId, targetId, activity, onSaved, onCancel }: {
  learnerId: string; targetId: string; activity: Activity | null; onSaved: () => void; onCancel: () => void;
}) {
  const t = useT();
  const [kind, setKind] = useState<ActivityKind>(activity?.kind ?? 'PROGRAM');
  const [programId, setProgramId] = useState(activity?.programId ?? '');
  const [title, setTitle] = useState(activity?.title ?? '');
  const [instructions, setInstructions] = useState(activity?.instructions ?? '');
  const [measure, setMeasure] = useState<Measure>(activity?.measure ?? 'TRIALS');
  const [changeNote, setChangeNote] = useState('');
  const [programs, setPrograms] = useState<ProgramView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = activity?.id ?? `new-${targetId}`;

  useEffect(() => {
    if (kind !== 'PROGRAM' || programs) return;
    api<ProgramView[]>('/programs?learnerId=' + encodeURIComponent(learnerId), { token: true })
      .then(setPrograms)
      .catch((e) => setError(e instanceof Error ? e.message : t('The programs could not be loaded.')));
  }, [kind, programs, learnerId, t]);

  function pickProgram(pid: string) {
    setProgramId(pid);
    const p = programs?.find((x) => x.id === pid);
    if (p) setTitle(p.name);
  }

  function fillTemplate() {
    const headings = TEMPLATE.map((h) => t(h)).join('\n\n\n');
    setInstructions(instructions.trim() ? `${instructions.trimEnd()}\n\n${headings}\n` : `${headings}\n`);
  }

  async function save() {
    setBusy(true);
    setError(null);
    const body = {
      changeNote: teachingChanged ? orNull(changeNote) : null,
      kind, programId: kind === 'PROGRAM' ? programId : null, title: title.trim(),
      instructions: orNull(instructions), measure: kind === 'PROGRAM' ? 'TRIALS' as Measure : measure,
    };
    try {
      if (activity) await updateActivity(learnerId, activity.id, body);
      else await createActivity(learnerId, targetId, body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That could not be saved just now.'));
      setBusy(false);
    }
  }

  const teachingChanged = !!activity && ((instructions.trim() || '') !== (activity.instructions ?? '').trim() || (kind !== 'PROGRAM' && measure !== activity.measure));
  const ready = title.trim() && (kind !== 'PROGRAM' || programId);

  return (
    <div className="planner-activity-editor">
      <h5>{activity ? t('Edit activity') : t('New activity')}</h5>
      <fieldset className="planner-activity-kinds">
        <legend className="field-label">{t('Kind of activity')}</legend>
        {(Object.keys(ACTIVITY_KIND_LABEL) as ActivityKind[]).map((k) => (
          <label key={k} className="check">
            <input type="radio" name={`activity-kind-${id}`} checked={kind === k} onChange={() => setKind(k)} />
            <span>{t(ACTIVITY_KIND_LABEL[k])}</span>
          </label>
        ))}
      </fieldset>
      {kind === 'PROGRAM' ? (
        <div className="field">
          <label htmlFor={`activity-program-${id}`}>{t('Program')}</label>
          {programs && programs.length === 0 ? (
            <p className="muted" id={`activity-program-${id}`}>{t('This learner has no programs yet. Make one on the Programs page first.')}</p>
          ) : (
            <select id={`activity-program-${id}`} value={programId} disabled={!programs} onChange={(e) => pickProgram(e.target.value)}>
              <option value="">{programs ? t('Choose a program') : t('Loading programs…')}</option>
              {programs?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
        </div>
      ) : (
        <div className="field">
          <label htmlFor={`activity-title-${id}`}>{t('Title')}</label>
          <input id={`activity-title-${id}`} type="text" maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />
          {kind === 'TEXT' && <p className="field-hint">{t('For example: Visit the park, practise waiting at the crossing.')}</p>}
        </div>
      )}
      <div className="field">
        <label htmlFor={`activity-instructions-${id}`}>{t('Instructions')}</label>
        <textarea id={`activity-instructions-${id}`} rows={8} maxLength={8000} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        <div className="toolbar">
          <button type="button" className="btn ghost" onClick={fillTemplate}>{t('Use a template')}</button>
        </div>
      </div>
      <div className="field">
        {kind === 'PROGRAM' ? (
          <>
            <span className="field-label">{t('How it is recorded')}</span>
            <p className="planner-activity-auto">{t('EasyLearning programs record every try automatically.')}</p>
          </>
        ) : (
          <>
            <label htmlFor={`activity-measure-${id}`}>{t('How it is recorded')}</label>
            <select id={`activity-measure-${id}`} value={measure} onChange={(e) => setMeasure(e.target.value as Measure)}>
              {(Object.keys(MEASURE_LABEL) as Measure[]).map((m) => <option key={m} value={m}>{t(MEASURE_LABEL[m])}</option>)}
            </select>
          </>
        )}
      </div>
      {teachingChanged && (
        <div className="field">
          <label htmlFor={`activity-change-${id}`}>{t('What changed and why (shown on the progress chart)')}</label>
          <input id={`activity-change-${id}`} type="text" maxLength={500} value={changeNote} onChange={(e) => setChangeNote(e.target.value)} />
          <p className="field-hint">{t('Optional. For example: Moved from full help to pointing, because 5 sessions in a row went well.')}</p>
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="button" className="btn" disabled={busy || !ready} onClick={() => void save()}>{t('Save activity')}</button>
        <button type="button" className="btn secondary" disabled={busy} onClick={onCancel}>{t('Cancel')}</button>
      </div>
    </div>
  );
}
